import Image from "next/image";
import Link from "next/link";
import { useCallback, useState } from "react";

import { cn } from "@/lib/utils";

export interface PlatformFeatureCard {
  icon: React.ElementType;
  title: string;
  description: string;
}

export interface PlatformTab {
  id: string;
  label: string;
  shortLabel: string;
  headline: string;
  description: string;
  phoneImage: string;
  ctaLink?: string;
  leftCards?: PlatformFeatureCard[];
  rightCards?: PlatformFeatureCard[];
  leftPhones?: { src: string; alt: string }[];
  rightPhones?: { src: string; alt: string }[];
}

interface PlatformDemoSectionContent {
  badge: string;
  heading: React.ReactNode;
  subtitle?: React.ReactNode;
  ctaLabel: string;
}

interface PlatformDemoSectionProps {
  tabs: PlatformTab[];
  content: PlatformDemoSectionContent;
}

function ArrowSVG({ className }: { className: string }) {
  return (
    <svg
      width="12"
      height="28"
      viewBox="0 0 12 28"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M0.0100035 6.67892L3.94579 13.5165L-0.00091452 20.332L3.8493 27.0209L11.6636 13.5267L3.87756 0.000131643L0.0100035 6.67892Z"
        fill="#FF5700"
      />
    </svg>
  );
}

function FeatureCardItem({ card }: { card: PlatformFeatureCard }) {
  const Icon = card.icon;
  return (
    <div className="bg-[rgba(67,67,67,0.4)] rounded-2xl px-4 py-4 lg:px-6 lg:py-8 flex flex-col gap-2 w-full lg:w-[250px]">
      <div className="flex items-center gap-2">
        <Icon className="w-6 h-6 text-white shrink-0" />
        <span className="text-white font-semibold text-sm md:text-base">{card.title}</span>
      </div>
      <p className="text-ash-400 text-xs leading-[1.4]">{card.description}</p>
    </div>
  );
}

export default function PlatformDemoSection({ tabs, content }: PlatformDemoSectionProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const tab = tabs[activeIndex];

  const goNext = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % tabs.length);
  }, [tabs.length]);

  const goPrev = useCallback(() => {
    setActiveIndex((prev) => (prev - 1 + tabs.length) % tabs.length);
  }, [tabs.length]);

  const hasFeatureCards = !!tab.leftCards || !!tab.rightCards;

  return (
    <div id="marketing-kit" className="w-full bg-linear-to-b from-ash-900 to-ash-700 overflow-hidden scroll-mt-24">
      <div className="flex flex-col items-center gap-4 md:gap-16 px-5 pt-16 max-w-screen-2xl mx-auto">
        {/* section header */}
        <div className="flex flex-col items-center md:gap-6 gap-4 text-center ">
          <div className="flex flex-col items-center gap-1 md:gap-4">
            <span className="text-xs font-bold uppercase tracking-[1.15px] text-rebolt-500">{content.badge}</span>
            <h2 className="font-clash font-medium text-4xl lg:text-6xl leading-[1.1] tracking-[-1.04px] text-white">
              {content.heading}
            </h2>
          </div>
        </div>

        <div className="flex flex-col items-center gap-8 w-full">
          <div className="flex flex-col items-center gap-6 md:gap-8 max-w-3xl mx-auto">
            {/* tab pills */}
            <div className="flex gap-1.5 items-center flex-wrap justify-center">
              {tabs.map((t, i) => (
                <button
                  key={t.id}
                  onClick={() => setActiveIndex(i)}
                  className={cn(
                    "h-[37px] px-4 md:px-5 rounded-full text-sm font-semibold transition-all cursor-pointer",
                    i === activeIndex
                      ? "bg-rebolt-500/15 border border-rebolt-500/35 text-[#ff6b2b]"
                      : "border border-white/10 text-clay-400/60 hover:text-clay-400 hover:border-white/20"
                  )}
                >
                  <span className="md:hidden">{t.shortLabel}</span>
                  <span className="hidden md:inline">{t.label}</span>
                </button>
              ))}
            </div>

            {/* headline with arrows + subtitle + description */}
            <div className="flex flex-col gap-4">
              <div className="flex gap-6 items-center justify-center">
                <button
                  onClick={goPrev}
                  className="text-rebolt-500 hover:text-rebolt-600 transition-colors cursor-pointer shrink-0"
                  aria-label="Previous tab"
                >
                  <ArrowSVG className="w-6 h-6 md:w-7 md:h-7 rotate-180" />
                </button>
                <h3
                  key={tab.id}
                  className="font-clash font-semibold text-xl md:text-4xl leading-none tracking-[-0.8px] text-rebolt-500 text-center animate-in fade-in duration-300"
                >
                  {tab.headline}
                </h3>
                <button
                  onClick={goNext}
                  className="text-rebolt-500 hover:text-rebolt-600 transition-colors cursor-pointer shrink-0"
                  aria-label="Next tab"
                >
                  <ArrowSVG className="w-6 h-6 md:w-7 md:h-7" />
                </button>
              </div>
              <div className="flex flex-col gap-3 md:gap-4">
                <p
                  key={`desc-${tab.id}`}
                  className="text-sm md:text-base text-white text-center leading-[1.4] animate-in fade-in duration-300"
                >
                  {tab.description}
                </p>
              </div>
            </div>
          </div>

          {/* mobile feature cards grid */}
          {hasFeatureCards && (
            <div
              key={`mobile-cards-${tab.id}`}
              className="grid grid-cols-2 gap-4 w-full lg:hidden animate-in fade-in duration-300"
            >
              {[...(tab.leftCards || []), ...(tab.rightCards || [])].map((card) => (
                <FeatureCardItem key={card.title} card={card} />
              ))}
            </div>
          )}

          {/* cta */}
          {tab.ctaLink && (
            <Link
              key={`cta-${tab.id}`}
              href={tab.ctaLink}
              className="inline-flex items-center justify-center h-9 px-6 rounded-full bg-ash-100 text-ash-900 text-sm font-semibold hover:bg-white transition-colors"
            >
              {content.ctaLabel}
            </Link>
          )}

          {/* demo area */}
          {hasFeatureCards ? (
            <>
              {/* desktop: flex layout with cards flanking phone */}
              <div
                key={`desktop-${tab.id}`}
                className="hidden lg:flex items-end justify-center gap-8 w-full animate-in fade-in duration-500"
              >
                {tab.leftCards && (
                  <div className="flex flex-col gap-4 w-62.5 pb-6 shrink-0">
                    {tab.leftCards.map((card) => (
                      <FeatureCardItem key={card.title} card={card} />
                    ))}
                  </div>
                )}
                <div className="relative w-100.5 aspect-402/551 shrink-0 rounded-t-3xl overflow-hidden shadow-md">
                  <Image src={tab.phoneImage} alt="App screen" fill className="object-cover object-top" />
                </div>
                {tab.rightCards && (
                  <div className="flex flex-col gap-4 w-62.5 pb-6 shrink-0">
                    {tab.rightCards.map((card) => (
                      <FeatureCardItem key={card.title} card={card} />
                    ))}
                  </div>
                )}
              </div>
              {/* mobile/tablet: just the center phone */}
              <div
                key={`mobile-${tab.id}`}
                className="lg:hidden relative w-full flex justify-center animate-in fade-in duration-500"
              >
                <div className="relative z-20 w-56.5 md:w-85 aspect-402/551 rounded-t-xl md:rounded-t-3xl overflow-hidden shadow-md">
                  <Image src={tab.phoneImage} alt="App screen" fill className="object-cover object-top" />
                </div>
              </div>
            </>
          ) : (
            <div key={tab.id} className="relative w-full flex justify-center animate-in fade-in duration-500">
              {/* center phone — in flow, sizes the container */}
              <div className="relative z-20 w-56.5 md:w-85 lg:w-100.5 aspect-402/551 rounded-t-xl md:rounded-t-3xl overflow-hidden shadow-md">
                <Image src={tab.phoneImage} alt="App screen" fill className="object-cover object-top" />
              </div>

              {tab.leftPhones?.map((phone) => (
                <div
                  key={phone.alt}
                  className="absolute left-0 md:left-[5%] lg:left-[12%] xl:left-[18%] bottom-0 z-10 w-43.5 md:w-65 lg:w-87 aspect-87/130 md:aspect-65/100 lg:aspect-87/125 rounded-t-xl md:rounded-t-20 overflow-hidden opacity-90 backdrop-blur-sm"
                >
                  <Image src={phone.src} alt={phone.alt} fill className="object-cover object-top" />
                </div>
              ))}
              {tab.rightPhones?.map((phone) => (
                <div
                  key={phone.alt}
                  className="absolute right-0 md:right-[5%] lg:right-[12%] xl:right-[18%] bottom-0 z-10 w-43.5 md:w-65 lg:w-87 aspect-87/120 md:aspect-65/95 lg:aspect-87/120 rounded-t-xl md:rounded-t-20 overflow-hidden opacity-90"
                >
                  <Image src={phone.src} alt={phone.alt} fill className="object-cover object-top" />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
